import { useState, useEffect, useMemo } from 'react';
import { useAdminSettings } from '@/hooks/useAdminSettings';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  X, 
  Copy, 
  Key, 
  Eye, 
  EyeOff,
  Mail,
  Globe,
  Calendar,
  CreditCard,
  Clock,
  BookOpen,
  CheckCircle,
  User,
  FileText,
  Pencil,
  Save,
  XCircle,
  Send,
  Inbox,
  Sparkles,
  Coins,
  Link,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { formatLastActive, getDaysUntilExpiration } from '@/services/clientService';

// Test passwords are no longer stored client-side.
// Admins can reset passwords via the standard auth flow if needed.

const getPlanOptions = (monthlyPrice: string, lifetimePrice: string) => [
  { value: 'Freemium', label: 'Freemium', price: 0 },
  { value: 'Monthly', label: 'Monthly', price: parseFloat(monthlyPrice) },
  { value: 'Lifetime', label: 'Lifetime', price: parseFloat(lifetimePrice) },
  { value: 'test_lifetime', label: 'Test Lifetime', price: parseFloat(lifetimePrice) },
  { value: 'test_monthly', label: 'Test Monthly', price: parseFloat(monthlyPrice) },
];

interface CRMClient {
  id: string;
  user_id: string | null;
  name: string;
  email: string;
  country: string | null;
  plan: string;
  plan_price: number;
  amount_paid: number | null;
  status: string;
  paid: boolean;
  has_access: boolean | null;
  subscription_start: string;
  subscription_end: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  subscription_status: string | null;
  motivation_letter: string | null;
  applied_at: string | null;
  last_active_at: string | null;
  created_at: string;
  updated_at: string;
  billing_name: string | null;
  billing_address: string | null;
}

interface AcademyProgress {
  academy_id: string;
  academy_name: string;
  completed_modules: number;
  total_modules: number;
}

interface ClassCompletion {
  class_id: string;
  score: number;
  total_questions: number;
  passed: boolean;
  completed_at: string;
}

interface ClientEmail {
  id: string;
  email_type: string;
  subject: string;
  sent_at: string;
  status: string;
}

interface AIUsageEntry {
  tool_type: string;
  credits_used: number;
  created_at: string;
}

interface AICreditsInfo {
  balance: number;
  monthly_allowance: number;
  total_used: number;
}

interface ClientInfoModalProps {
  client: CRMClient;
  onClose: () => void;
  onClientUpdated?: () => void;
}
const PAYMENT_PRODUCTS = [
  { key: 'monthly', label: 'Monthly Plan', price: '$19/mo', category: 'Plans' },
  { key: 'lifetime', label: 'Lifetime Plan', price: '$97', category: 'Plans' },
  { key: 'credits_starter', label: 'Starter', price: '$2.99 · 50 credits', category: 'Credit Packs' },
  { key: 'credits_growth', label: 'Growth', price: '$9.99 · 200 credits', category: 'Credit Packs' },
  { key: 'credits_pro', label: 'Pro', price: '$22.99 · 500 credits', category: 'Credit Packs' },
  { key: 'credits_elite', label: 'Elite', price: '$69.99 · 2000 credits', category: 'Credit Packs' },
  { key: 'credits_ultimate', label: 'Ultimate', price: '$499 · 35000 credits', category: 'Credit Packs' },
];

const PaymentLinksSection = ({ clientEmail, clientId }: { clientEmail: string; clientId: string }) => {
  const [generatingLink, setGeneratingLink] = useState<string | null>(null);
  const [generatedLinks, setGeneratedLinks] = useState<Record<string, string>>({});

  const generateLink = async (product: string) => {
    setGeneratingLink(product);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('generate-payment-link', {
        body: { product, clientEmail, clientId },
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      if (data?.url) {
        setGeneratedLinks(prev => ({ ...prev, [product]: data.url }));
        navigator.clipboard.writeText(data.url);
        toast.success(`Link copied! Valid for 24h`);
      }
    } catch (err) {
      console.error('Error generating payment link:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to generate link');
    } finally {
      setGeneratingLink(null);
    }
  };

  const copyLink = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  const categories = ['Plans', 'Credit Packs'];

  return (
    <div className="mt-6 space-y-4">
      <h4 className="text-sm font-medium text-primary uppercase tracking-wider flex items-center gap-2">
        <Link className="w-4 h-4" /> Payment Links
      </h4>
      <div className="p-4 bg-muted/10 rounded-lg border border-border/20">
        <p className="text-xs text-muted-foreground mb-4">
          Generate Stripe checkout links pre-filled with this client's email. Links expire in 24 hours.
        </p>
        {categories.map(category => (
          <div key={category} className="mb-4 last:mb-0">
            <p className="text-xs font-medium text-foreground mb-2">{category}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {PAYMENT_PRODUCTS.filter(p => p.category === category).map(product => {
                const link = generatedLinks[product.key];
                const isGenerating = generatingLink === product.key;
                return (
                  <div key={product.key} className="flex items-center gap-2 p-2 bg-background/50 rounded border border-border/20">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-foreground">{product.label}</p>
                      <p className="text-[10px] text-muted-foreground">{product.price}</p>
                    </div>
                    {link ? (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-primary"
                          onClick={() => copyLink(link)}
                          title="Copy link"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground"
                          onClick={() => window.open(link, '_blank')}
                          title="Open link"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => generateLink(product.key)}
                        disabled={isGenerating}
                      >
                        {isGenerating ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <>
                            <Link className="w-3 h-3 mr-1" />
                            Generate
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const ClientInfoModal = ({ client, onClose, onClientUpdated }: ClientInfoModalProps) => {

  const { monthlyPrice, lifetimePrice } = useAdminSettings();
  const PLAN_OPTIONS = useMemo(() => getPlanOptions(monthlyPrice, lifetimePrice), [monthlyPrice, lifetimePrice]);
  const [showPassword, setShowPassword] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    name: client.name,
    email: client.email,
    plan: client.plan,
    paid: client.paid,
    has_access: client.has_access ?? false,
    status: client.status,
  });
  const [expandedData, setExpandedData] = useState<{
    academyProgress: AcademyProgress[];
    classCompletions: ClassCompletion[];
    emails: ClientEmail[];
    aiUsage: AIUsageEntry[];
    aiCredits: AICreditsInfo | null;
    loading: boolean;
  }>({ academyProgress: [], classCompletions: [], emails: [], aiUsage: [], aiCredits: null, loading: true });

  useEffect(() => {
    loadClientData();
  }, [client.id]);

  useEffect(() => {
    // Reset edit form when client changes
    setEditForm({
      name: client.name,
      email: client.email,
      plan: client.plan,
      paid: client.paid,
      has_access: client.has_access ?? false,
      status: client.status,
    });
    setIsEditing(false);
  }, [client]);

  const loadClientData = async () => {
    try {
      const [progressRes, completionsRes, emailsRes, aiUsageRes, aiCreditsRes] = await Promise.all([
        supabase
          .from('client_academy_progress')
          .select('academy_id, academy_name, completed_modules, total_modules')
          .eq('client_id', client.id),
        supabase
          .from('class_completions')
          .select('class_id, score, total_questions, passed, completed_at')
          .eq('client_id', client.id)
          .order('completed_at', { ascending: false })
          .limit(20),
        supabase
          .from('client_emails')
          .select('id, email_type, subject, sent_at, status')
          .eq('client_id', client.id)
          .order('sent_at', { ascending: false })
          .limit(50),
        supabase
          .from('ai_usage_log')
          .select('tool_type, credits_used, created_at')
          .eq('client_id', client.id)
          .order('created_at', { ascending: false })
          .limit(200),
        supabase
          .from('ai_credits')
          .select('balance, monthly_allowance, total_used')
          .eq('client_id', client.id)
          .single(),
      ]);

      setExpandedData({
        academyProgress: progressRes.data || [],
        classCompletions: completionsRes.data || [],
        emails: emailsRes.data || [],
        aiUsage: aiUsageRes.data || [],
        aiCredits: aiCreditsRes.data || null,
        loading: false
      });
    } catch (error) {
      console.error('Error loading client data:', error);
      setExpandedData({ academyProgress: [], classCompletions: [], emails: [], aiUsage: [], aiCredits: null, loading: false });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };


  const getStatusLabel = () => {
    if (!client.paid) return 'Pending Payment';
    const days = getDaysUntilExpiration(client.subscription_end);
    if (days < 0) return 'Expired';
    if (days <= 30) return 'Expiring Soon';
    return 'Active';
  };

  const getStatusColor = () => {
    if (!client.paid) return 'bg-yellow-500/20 text-yellow-400';
    const days = getDaysUntilExpiration(client.subscription_end);
    if (days < 0) return 'bg-red-500/20 text-red-400';
    if (days <= 30) return 'bg-orange-500/20 text-orange-400';
    return 'bg-green-500/20 text-green-400';
  };

  const getClientTypeLabel = () => {
    const plan = client.plan?.toLowerCase() || '';
    if (plan === 'test_lifetime' || plan === 'test-lifetime') return 'Test Client Admin';
    if (plan === 'test_monthly' || plan === 'test-monthly') return 'Test Monthly Admin';
    if (plan === 'admin') return 'Admin';
    if (!client.paid) return 'Freemium';
    if (plan === 'lifetime') return 'Lifetime';
    if (plan === 'monthly') return 'Monthly';
    return plan || 'Freemium';
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const selectedPlan = PLAN_OPTIONS.find(p => p.value === editForm.plan);
      const planPrice = selectedPlan?.price ?? client.plan_price;
      
      // Determine paid status and access based on plan
      const isPaid = editForm.plan !== 'Freemium';
      const hasAccess = isPaid;
      const status = isPaid ? 'active' : 'inactive';
      
      const { error } = await supabase
        .from('clients')
        .update({
          name: editForm.name,
          email: editForm.email,
          plan: editForm.plan,
          plan_price: planPrice,
          paid: isPaid,
          has_access: hasAccess,
          status: status,
        })
        .eq('id', client.id);

      if (error) throw error;

      toast.success('Client updated successfully');
      setIsEditing(false);
      onClientUpdated?.();
    } catch (error) {
      console.error('Error updating client:', error);
      toast.error('Failed to update client');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditForm({
      name: client.name,
      email: client.email,
      plan: client.plan,
      paid: client.paid,
      has_access: client.has_access ?? false,
      status: client.status,
    });
    setIsEditing(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="w-full max-w-4xl bg-card border border-border/30 rounded-xl shadow-2xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border/20">
          <div className="flex-1">
            {isEditing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-name" className="text-xs text-muted-foreground">Name</Label>
                    <Input
                      id="edit-name"
                      value={editForm.name}
                      onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                      className="h-9 bg-background/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-email" className="text-xs text-muted-foreground">Email</Label>
                    <Input
                      id="edit-email"
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                      className="h-9 bg-background/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-plan" className="text-xs text-muted-foreground">Plan</Label>
                    <Select
                      value={editForm.plan}
                      onValueChange={(value) => setEditForm(prev => ({ ...prev, plan: value }))}
                    >
                      <SelectTrigger className="h-9 bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border border-border z-[100]">
                        {PLAN_OPTIONS.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label} (${option.price})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <h3 className="text-xl font-light text-foreground">{client.name}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-muted-foreground">{client.email}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor()}`}>
                    {getStatusLabel()}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary">
                    {getClientTypeLabel()}
                  </span>
                </div>
              </>
            )}
          </div>
          <div className="flex items-center gap-2 ml-4">
            {isEditing ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Cancel
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSave}
                  disabled={isSaving}
                  className="bg-primary hover:bg-primary/90"
                >
                  <Save className="w-4 h-4 mr-1" />
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Pencil className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="w-5 h-5" />
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Account & Auth */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-primary uppercase tracking-wider flex items-center gap-2">
                <User className="w-4 h-4" /> Account & Auth
              </h4>
              <div className="space-y-3 p-4 bg-muted/10 rounded-lg border border-border/20">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Client ID</span>
                  <div className="flex items-center gap-2">
                    <code className="text-xs font-mono text-foreground">{client.id.slice(0, 8)}...</code>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(client.id)}>
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">User ID</span>
                  <div className="flex items-center gap-2">
                    <code className="text-xs font-mono text-foreground">{client.user_id ? `${client.user_id.slice(0, 8)}...` : 'N/A'}</code>
                    {client.user_id && (
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(client.user_id!)}>
                        <Copy className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Email</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-foreground">{client.email}</span>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(client.email)}>
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Has Access</span>
                  <span className={`text-xs ${client.has_access ? 'text-green-400' : 'text-red-400'}`}>
                    {client.has_access ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>

            {/* Dates & Activity */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-primary uppercase tracking-wider flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Dates & Activity
              </h4>
              <div className="space-y-3 p-4 bg-muted/10 rounded-lg border border-border/20">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Created At</span>
                  <span className="text-xs text-foreground">{new Date(client.created_at).toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Applied At</span>
                  <span className="text-xs text-foreground">{client.applied_at ? new Date(client.applied_at).toLocaleString() : 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Subscription Start</span>
                  <span className="text-xs text-foreground">{client.subscription_start}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Subscription End</span>
                  <span className="text-xs text-foreground">{client.subscription_end}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Last Active</span>
                  <span className="text-xs text-foreground">{formatLastActive(client.last_active_at)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Updated At</span>
                  <span className="text-xs text-foreground">{new Date(client.updated_at).toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Payment & Stripe */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-primary uppercase tracking-wider flex items-center gap-2">
                <CreditCard className="w-4 h-4" /> Payment & Stripe
              </h4>
              <div className="space-y-3 p-4 bg-muted/10 rounded-lg border border-border/20">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Plan</span>
                  <span className="text-xs text-foreground">{client.plan}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Plan Price</span>
                  <span className="text-xs text-foreground">
                    {client.plan === 'Monthly' ? '$19/mo' : client.plan === 'Lifetime' ? '$97' : client.plan === 'Freemium' ? 'Free' : `$${client.plan_price}`}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Amount Paid (Access)</span>
                  <span className={`text-xs font-medium ${client.amount_paid != null ? 'text-green-400' : 'text-muted-foreground'}`}>
                    {client.amount_paid != null ? `$${Number(client.amount_paid).toFixed(2)}` : 'N/A'}
                    {client.amount_paid != null && client.amount_paid < client.plan_price && (
                      <span className="ml-1 text-yellow-400">(discount)</span>
                    )}
                  </span>
                </div>
                {/* Credit Purchases */}
                {(() => {
                  const CREDIT_PACK_AMOUNTS: Record<number, string> = {
                    50: 'Starter ($2.99)',
                    200: 'Growth ($9.99)',
                    500: 'Pro ($22.99)',
                    2000: 'Elite ($69.99)',
                    35000: 'Ultimate ($499)',
                  };
                  const CREDIT_PACK_PRICES: Record<number, number> = {
                    50: 2.99, 200: 9.99, 500: 22.99, 2000: 69.99, 35000: 499,
                  };
                  const creditPurchases = expandedData.aiUsage
                    .filter(e => e.tool_type.startsWith('credit_purchase_'))
                    .map(e => ({
                      credits: Math.abs(e.credits_used),
                      date: e.created_at,
                      packName: CREDIT_PACK_AMOUNTS[Math.abs(e.credits_used)] || `${Math.abs(e.credits_used)} credits`,
                      price: CREDIT_PACK_PRICES[Math.abs(e.credits_used)] || 0,
                    }));
                  const totalCreditSpent = creditPurchases.reduce((sum, p) => sum + p.price, 0);
                  const totalSpent = (client.amount_paid || 0) + totalCreditSpent;

                  return (
                    <>
                      <div className="flex items-center justify-between pt-2 border-t border-border/20">
                        <span className="text-xs text-muted-foreground">Credit Pack Purchases</span>
                        <span className="text-xs font-medium text-foreground">
                          {creditPurchases.length > 0 ? `${creditPurchases.length} purchase${creditPurchases.length > 1 ? 's' : ''}` : 'None'}
                        </span>
                      </div>
                      {creditPurchases.length > 0 && (
                        <div className="space-y-1 ml-2">
                          {creditPurchases.map((p, idx) => (
                            <div key={idx} className="flex items-center justify-between text-xs">
                              <span className="text-muted-foreground flex items-center gap-1">
                                <Coins className="w-3 h-3 text-amber-500" />
                                {p.packName}
                              </span>
                              <span className="text-muted-foreground">{new Date(p.date).toLocaleDateString()}</span>
                            </div>
                          ))}
                          <div className="flex items-center justify-between text-xs pt-1">
                            <span className="text-muted-foreground">Credit Packs Total</span>
                            <span className="text-amber-400 font-medium">${totalCreditSpent.toFixed(2)}</span>
                          </div>
                        </div>
                      )}
                      <div className="flex items-center justify-between pt-2 border-t border-border/20">
                        <span className="text-xs font-medium text-foreground">Total Spent</span>
                        <span className="text-sm font-bold text-green-400">${totalSpent.toFixed(2)}</span>
                      </div>
                    </>
                  );
                })()}
                <div className="flex items-center justify-between pt-2 border-t border-border/20">
                  <span className="text-xs text-muted-foreground">Paid</span>
                  <span className={`text-xs ${client.paid ? 'text-green-400' : 'text-red-400'}`}>
                    {client.paid ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Subscription Status</span>
                  <span className="text-xs text-foreground">{client.subscription_status || 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Stripe Customer</span>
                  <div className="flex items-center gap-2">
                    <code className="text-xs font-mono text-foreground">{client.stripe_customer_id ? `${client.stripe_customer_id.slice(0, 12)}...` : 'N/A'}</code>
                    {client.stripe_customer_id && (
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(client.stripe_customer_id!)}>
                        <Copy className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Stripe Sub ID</span>
                  <div className="flex items-center gap-2">
                    <code className="text-xs font-mono text-foreground">{client.stripe_subscription_id ? `${client.stripe_subscription_id.slice(0, 12)}...` : 'N/A'}</code>
                    {client.stripe_subscription_id && (
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(client.stripe_subscription_id!)}>
                        <Copy className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Billing Name</span>
                  <span className="text-xs text-foreground">{client.billing_name || 'N/A'}</span>
                </div>
                <div className="flex items-start justify-between">
                  <span className="text-xs text-muted-foreground">Billing Address</span>
                  <span className="text-xs text-foreground text-right max-w-[200px]">{client.billing_address || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Application Info */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-primary uppercase tracking-wider flex items-center gap-2">
                <FileText className="w-4 h-4" /> Application Info
              </h4>
              <div className="space-y-3 p-4 bg-muted/10 rounded-lg border border-border/20">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Country</span>
                  <span className="text-xs text-foreground flex items-center gap-1">
                    <Globe className="w-3 h-3" /> {client.country || 'N/A'}
                  </span>
                </div>
                {client.motivation_letter && (
                  <div className="pt-3 border-t border-border/20">
                    <span className="text-xs text-muted-foreground block mb-2">Motivation Letter</span>
                    <p className="text-xs text-foreground whitespace-pre-wrap bg-background/50 p-3 rounded border border-border/20 max-h-32 overflow-y-auto">
                      {client.motivation_letter}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Payment Links Section */}
          <PaymentLinksSection clientEmail={client.email} clientId={client.id} />

          {/* Academy Progress */}
          <div className="mt-6 space-y-4">
            <h4 className="text-sm font-medium text-primary uppercase tracking-wider flex items-center gap-2">
              <BookOpen className="w-4 h-4" /> Academy Progress
            </h4>
            <div className="p-4 bg-muted/10 rounded-lg border border-border/20">
              {expandedData.loading ? (
                <p className="text-xs text-muted-foreground">Loading progress...</p>
              ) : expandedData.academyProgress.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {expandedData.academyProgress.map((progress) => (
                    <div key={progress.academy_id} className="p-3 bg-background/50 rounded border border-border/20">
                      <p className="text-xs font-medium text-foreground mb-2">{progress.academy_name}</p>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-muted/30 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary rounded-full"
                            style={{ width: `${(progress.completed_modules / progress.total_modules) * 100}%` }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {progress.completed_modules}/{progress.total_modules}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No academy progress yet</p>
              )}
            </div>
          </div>

          {/* AI Usage */}
          <div className="mt-6 space-y-4">
            <h4 className="text-sm font-medium text-primary uppercase tracking-wider flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> AI Usage
            </h4>
            <div className="p-4 bg-muted/10 rounded-lg border border-border/20">
              {expandedData.loading ? (
                <p className="text-xs text-muted-foreground">Loading AI usage...</p>
              ) : (
                <>
              {/* Credits Summary + Gift Button */}
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-muted-foreground">Credit Management</span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-7 border-amber-500/40 text-amber-500 hover:bg-amber-500/10"
                      onClick={async () => {
                        try {
                          // Check if credits row exists
                          const { data: existing } = await supabase
                            .from('ai_credits')
                            .select('id, balance')
                            .eq('client_id', client.id)
                            .maybeSingle();

                          if (existing) {
                            const { error } = await supabase
                              .from('ai_credits')
                              .update({ balance: existing.balance + 10, updated_at: new Date().toISOString() })
                              .eq('client_id', client.id);
                            if (error) throw error;
                          } else {
                            const { error } = await supabase
                              .from('ai_credits')
                              .insert({ client_id: client.id, balance: 10, monthly_allowance: 0, total_used: 0 });
                            if (error) throw error;
                          }

                          // Log as gift in ai_usage_log
                          await supabase.from('ai_usage_log').insert({
                            client_id: client.id,
                            tool_type: 'credit_gift_admin',
                            credits_used: -10,
                          });

                          toast.success('Gifted 10 credits to ' + client.name);
                          loadClientData();
                        } catch (err) {
                          console.error('Gift credits error:', err);
                          toast.error('Failed to gift credits');
                        }
                      }}
                    >
                      <Coins className="w-3 h-3 mr-1" />
                      Gift 10 Credits
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    <div className="p-3 bg-background/50 rounded border border-border/20 text-center">
                      <p className="text-lg font-bold text-foreground">{expandedData.aiCredits?.balance ?? 0}</p>
                      <p className="text-[10px] text-muted-foreground">Balance</p>
                    </div>
                    <div className="p-3 bg-background/50 rounded border border-border/20 text-center">
                      <p className="text-lg font-bold text-foreground">{expandedData.aiCredits?.total_used ?? 0}</p>
                      <p className="text-[10px] text-muted-foreground">Total Used</p>
                    </div>
                    <div className="p-3 bg-background/50 rounded border border-border/20 text-center">
                      <p className="text-lg font-bold text-foreground">{expandedData.aiCredits?.monthly_allowance ?? 0}</p>
                      <p className="text-[10px] text-muted-foreground">Monthly Allow.</p>
                    </div>
                    <div className="p-3 bg-background/50 rounded border border-border/20 text-center">
                      {(() => {
                        const plan = client.plan?.toLowerCase() || 'freemium';
                        const allowance = plan === 'lifetime' ? 500 : plan === 'monthly' ? 100 : 10;
                        const totalUsed = expandedData.aiCredits?.total_used ?? 0;
                        const ratio = allowance > 0 ? totalUsed / allowance : 0;
                        const level = ratio > 2 ? 'high' : ratio > 0.5 ? 'medium' : 'low';
                        const cost = (totalUsed * 0.01).toFixed(2);
                        return (
                          <>
                            <p className={`text-lg font-bold ${level === 'high' ? 'text-red-500' : level === 'medium' ? 'text-amber-500' : 'text-green-500'}`}>
                              ${cost}
                            </p>
                            <p className="text-[10px] text-muted-foreground">Est. API Cost</p>
                            <span className={`text-[9px] mt-1 inline-block px-1.5 py-0.5 rounded-full font-bold ${
                              level === 'high' ? 'bg-red-100 text-red-600' : level === 'medium' ? 'bg-amber-100 text-amber-600' : 'bg-green-100 text-green-600'
                            }`}>
                              {level === 'high' ? '🔥 HIGH' : level === 'medium' ? '📊 MED' : '✅ LOW'} USAGE
                            </span>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Recent Usage Log */}
                  {expandedData.aiUsage.length > 0 ? (
                    <div className="space-y-1.5 max-h-40 overflow-y-auto">
                      {expandedData.aiUsage.slice(0, 20).map((entry, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-background/50 rounded border border-border/20">
                          <div className="flex items-center gap-2">
                            <Coins className="w-3 h-3 text-amber-500" />
                            <span className="text-xs text-foreground capitalize">{entry.tool_type.replace(/-/g, ' ')}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-xs font-medium text-foreground">{entry.credits_used} cr</span>
                            <span className="text-[10px] text-muted-foreground">{new Date(entry.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground text-center">No AI tool usage yet</p>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Recent Class Completions */}
          <div className="mt-6 space-y-4">
            <h4 className="text-sm font-medium text-primary uppercase tracking-wider flex items-center gap-2">
              <CheckCircle className="w-4 h-4" /> Recent Class Completions
            </h4>
            <div className="p-4 bg-muted/10 rounded-lg border border-border/20">
              {expandedData.loading ? (
                <p className="text-xs text-muted-foreground">Loading completions...</p>
              ) : expandedData.classCompletions.length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {expandedData.classCompletions.map((completion, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-background/50 rounded border border-border/20">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${completion.passed ? 'bg-green-400' : 'bg-red-400'}`} />
                        <span className="text-xs text-foreground">
                          Score: {completion.score}/{completion.total_questions}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(completion.completed_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No class completions yet</p>
              )}
            </div>
          </div>

          {/* Email History */}
          <div className="mt-6 space-y-4">
            <h4 className="text-sm font-medium text-primary uppercase tracking-wider flex items-center gap-2">
              <Inbox className="w-4 h-4" /> Email History
            </h4>
            <div className="p-4 bg-muted/10 rounded-lg border border-border/20">
              {expandedData.loading ? (
                <p className="text-xs text-muted-foreground">Loading emails...</p>
              ) : expandedData.emails.length > 0 ? (
                <ScrollArea className="max-h-64">
                  <div className="space-y-2">
                    {expandedData.emails.map((email) => (
                      <div key={email.id} className="flex items-start justify-between p-3 bg-background/50 rounded border border-border/20">
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-primary/10 rounded">
                            <Send className="w-3 h-3 text-primary" />
                          </div>
                          <div>
                            <p className="text-xs font-medium text-foreground">{email.subject}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary capitalize">
                                {email.email_type.replace(/_/g, ' ')}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                email.status === 'sent' 
                                  ? 'bg-green-500/20 text-green-400' 
                                  : 'bg-yellow-500/20 text-yellow-400'
                              }`}>
                                {email.status}
                              </span>
                            </div>
                          </div>
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(email.sent_at).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-6">
                  <Inbox className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">No emails sent to this client yet</p>
                  <p className="text-xs text-muted-foreground/60 mt-1">
                    Use Quick Actions to send emails
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ClientInfoModal;
